import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, BarChart3, Clock, Target, Share2, FileText, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { TradePostMortem, type PostMortemTrade } from './TradePostMortem';

interface StrategyAnalyticsPageProps {
  instanceId: string;
  onBack?: () => void;
}

interface FilledOrder {
  id: string;
  symbol: string;
  side: string;
  price: number | null;
  quantity: number;
  status: string;
  created_at: string;
}

interface StrategyPosition {
  id: string;
  symbol: string;
  side: string;
  average_entry_price: number;
  realized_pnl: number;
  opened_at: string;
  closed_at: string | null;
  status: string;
}

const StrategyAnalyticsPage: React.FC<StrategyAnalyticsPageProps> = ({ instanceId, onBack }) => {
  const { user } = useAuth();
  const [instance, setInstance] = useState<any>(null);
  const [orders, setOrders] = useState<FilledOrder[]>([]);
  const [positions, setPositions] = useState<StrategyPosition[]>([]);
  const [performance, setPerformance] = useState<any>(null);
  const [allPerformances, setAllPerformances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !instanceId) return;
    const fetchData = async () => {
      setLoading(true);
      const [instRes, ordRes, posRes, perfRes, allPerfRes] = await Promise.all([
        supabase.from('strategy_instances').select('*').eq('id', instanceId).single(),
        supabase.from('strategy_orders').select('*').eq('strategy_instance_id', instanceId).eq('status', 'filled').order('created_at', { ascending: true }),
        supabase.from('strategy_positions').select('*').eq('strategy_instance_id', instanceId).order('opened_at', { ascending: true }),
        supabase.from('strategy_performance').select('*').eq('strategy_instance_id', instanceId).single(),
        supabase.from('strategy_performance').select('*, strategy_instances!inner(strategy_name, status, execution_mode)').eq('strategy_instances.user_id', user.id),
      ]);
      if (instRes.data) setInstance(instRes.data);
      if (ordRes.data) setOrders(ordRes.data as FilledOrder[]);
      if (posRes.data) setPositions(posRes.data as StrategyPosition[]);
      if (perfRes.data) setPerformance(perfRes.data);
      if (allPerfRes.data) setAllPerformances(allPerfRes.data);
      setLoading(false);
    };
    fetchData();
  }, [user, instanceId]);

  // Compute cumulative PnL from closed positions
  const pnlChartData = useMemo(() => {
    const closed = positions.filter(p => p.status === 'closed' && p.closed_at);
    let cumulative = 0;
    return closed.map(p => {
      cumulative += p.realized_pnl;
      return {
        date: new Date(p.closed_at!).toLocaleDateString(),
        pnl: Number(cumulative.toFixed(2)),
      };
    });
  }, [positions]);

  // Trade distribution histogram
  const tradeDistribution = useMemo(() => {
    const closed = positions.filter(p => p.status === 'closed');
    const buckets = [
      { label: '< -5%', min: -Infinity, max: -5, count: 0 },
      { label: '-5% to -2%', min: -5, max: -2, count: 0 },
      { label: '-2% to 0%', min: -2, max: 0, count: 0 },
      { label: '0% to 2%', min: 0, max: 2, count: 0 },
      { label: '2% to 5%', min: 2, max: 5, count: 0 },
      { label: '> 5%', min: 5, max: Infinity, count: 0 },
    ];
    closed.forEach(p => {
      const pctReturn = p.average_entry_price > 0 ? (p.realized_pnl / p.average_entry_price) * 100 : 0;
      for (const b of buckets) {
        if (pctReturn >= b.min && pctReturn < b.max) { b.count++; break; }
      }
    });
    return buckets.map(b => ({ name: b.label, count: b.count, fill: b.max <= 0 ? 'hsl(var(--destructive))' : 'hsl(var(--primary))' }));
  }, [positions]);

  // Holding time analysis
  const holdingTimeStats = useMemo(() => {
    const closed = positions.filter(p => p.status === 'closed' && p.closed_at);
    if (closed.length === 0) return { avg: 0, data: [] };
    const durations = closed.map(p => {
      const ms = new Date(p.closed_at!).getTime() - new Date(p.opened_at).getTime();
      return ms / (1000 * 60 * 60);
    });
    const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
    const buckets = [
      { label: '< 1h', min: 0, max: 1, count: 0 },
      { label: '1-6h', min: 1, max: 6, count: 0 },
      { label: '6-24h', min: 6, max: 24, count: 0 },
      { label: '1-7d', min: 24, max: 168, count: 0 },
      { label: '> 7d', min: 168, max: Infinity, count: 0 },
    ];
    durations.forEach(d => {
      for (const b of buckets) {
        if (d >= b.min && d < b.max) { b.count++; break; }
      }
    });
    return { avg, data: buckets.map(b => ({ name: b.label, count: b.count })) };
  }, [positions]);

  // Worst 3 trades for post-mortem
  const worstTrades = useMemo((): PostMortemTrade[] => {
    return positions
      .filter(p => p.status === 'closed' && p.realized_pnl < 0)
      .sort((a, b) => a.realized_pnl - b.realized_pnl)
      .slice(0, 3)
      .map(p => ({
        id: p.id,
        symbol: p.symbol,
        side: p.side,
        average_entry_price: p.average_entry_price,
        realized_pnl: p.realized_pnl,
        opened_at: p.opened_at,
        closed_at: p.closed_at,
        strategy_instance_id: (p as any).strategy_instance_id ?? instanceId ?? null,
      }));
  }, [positions, instanceId]);

  // Sort state for trades table
  const [sortField, setSortField] = useState<'date' | 'pnl'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Publish modal state
  const [showPublish, setShowPublish] = useState(false);
  const [pubName, setPubName] = useState('');
  const [pubDesc, setPubDesc] = useState('');
  const [pubRisk, setPubRisk] = useState('medium');
  const [pubAssets, setPubAssets] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);

  const sortedPositions = useMemo(() => {
    const closed = positions.filter(p => p.status === 'closed');
    return [...closed].sort((a, b) => {
      if (sortField === 'pnl') return sortDir === 'asc' ? a.realized_pnl - b.realized_pnl : b.realized_pnl - a.realized_pnl;
      const da = new Date(a.closed_at || a.opened_at).getTime();
      const db = new Date(b.closed_at || b.opened_at).getTime();
      return sortDir === 'asc' ? da - db : db - da;
    });
  }, [positions, sortField, sortDir]);

  const toggleSort = (field: 'date' | 'pnl') => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <p>Strategy instance not found.</p>
        {onBack && <Button variant="outline" onClick={onBack} className="mt-4">Go Back</Button>}
      </div>
    );
  }

  const metrics = performance || { sharpe_ratio: 0, max_drawdown: 0, win_rate: 0, net_profit: 0, total_trades: 0 };
  const canPublish = (metrics.total_trades || 0) >= 5;

  const handlePublish = async () => {
    if (!user || !instance) return;
    if (!pubName.trim() || !pubDesc.trim()) {
      toast({ title: 'Missing fields', description: 'Name and description are required.', variant: 'destructive' });
      return;
    }
    setPublishing(true);
    try {
      const { error } = await supabase.from('marketplace_strategies').upsert({
        publisher_id: user.id,
        strategy_instance_id: instanceId,
        name: pubName,
        description: pubDesc,
        risk_level: pubRisk,
        asset_classes: pubAssets,
        is_published: true,
        published_at: new Date().toISOString(),
        sharpe_ratio: metrics.sharpe_ratio || 0,
        max_drawdown: metrics.max_drawdown || 0,
        win_rate: metrics.win_rate || 0,
        total_return: metrics.net_profit || 0,
        total_trades: metrics.total_trades || 0,
      }, { onConflict: 'strategy_instance_id' });
      if (error) throw error;
      toast({ title: 'Published!', description: 'Your strategy is now on the marketplace.' });
      setShowPublish(false);
    } catch (err: any) {
      toast({ title: 'Publish Failed', description: err.message, variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  const ASSET_OPTIONS = ['Forex', 'Crypto', 'Stocks', 'Commodities'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-foreground">{instance.strategy_name}</h2>
            <p className="text-sm text-muted-foreground">
              Deployed {new Date(instance.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const { data, error } = await supabase.functions.invoke('generate-report', {
                  body: { report_type: 'strategy', strategy_instance_id: instanceId },
                });
                if (error) throw error;
                const blob = new Blob([data.html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const win = window.open(url, '_blank');
                if (win) { win.onload = () => { win.print(); URL.revokeObjectURL(url); }; }
              } catch (err) { console.error('Export failed:', err); }
            }}
            className="gap-1"
          >
            <FileText className="h-3.5 w-3.5" />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPublish(true)}
            disabled={!canPublish}
            className="gap-1"
          >
            <Share2 className="h-3.5 w-3.5" />
            Publish
          </Button>
          <Badge variant={instance.status === 'running' ? 'default' : 'secondary'} className="capitalize">
            {instance.status}
          </Badge>
          <Badge variant="outline" className="capitalize">
            {instance.execution_mode}
          </Badge>
        </div>
      </div>

      {/* Publish Modal */}
      <Dialog open={showPublish} onOpenChange={setShowPublish}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish to Marketplace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Strategy Name</Label>
              <Input value={pubName} onChange={e => setPubName(e.target.value)} placeholder="My Strategy" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={pubDesc} onChange={e => setPubDesc(e.target.value)} placeholder="Describe your strategy..." rows={3} />
            </div>
            <div>
              <Label>Risk Level</Label>
              <Select value={pubRisk} onValueChange={setPubRisk}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Asset Classes</Label>
              <div className="flex flex-wrap gap-3 mt-1">
                {ASSET_OPTIONS.map(a => (
                  <label key={a} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={pubAssets.includes(a)}
                      onCheckedChange={checked => {
                        setPubAssets(prev => checked ? [...prev, a] : prev.filter(x => x !== a));
                      }}
                    />
                    {a}
                  </label>
                ))}
              </div>
            </div>
            {!canPublish && (
              <p className="text-xs text-destructive">Strategy needs at least 5 completed trades to publish.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublish(false)}>Cancel</Button>
            <Button onClick={handlePublish} disabled={publishing || !canPublish}>
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Sharpe Ratio', value: metrics.sharpe_ratio?.toFixed(2) ?? '0.00', icon: Target, color: 'text-primary' },
          { label: 'Max Drawdown', value: `${(metrics.max_drawdown * 100)?.toFixed(1) ?? '0.0'}%`, icon: TrendingDown, color: 'text-destructive' },
          { label: 'Win Rate', value: `${(metrics.win_rate * 100)?.toFixed(1) ?? '0.0'}%`, icon: Activity, color: 'text-primary' },
          { label: 'Total PnL', value: `$${metrics.net_profit?.toFixed(2) ?? '0.00'}`, icon: TrendingUp, color: metrics.net_profit >= 0 ? 'text-primary' : 'text-destructive' },
        ].map(m => (
          <Card key={m.label} className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <m.icon className={`h-4 w-4 ${m.color}`} />
                <span className="text-xs text-muted-foreground">{m.label}</span>
              </div>
              <p className={`text-xl font-bold font-mono-data ${m.color}`}>{m.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* PnL Chart */}
      <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Cumulative PnL
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pnlChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={pnlChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }} />
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                <defs>
                  <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="pnl" stroke="hsl(var(--primary))" fill="url(#pnlGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No closed trades yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Trade Distribution + Holding Time */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-lg">Trade Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {tradeDistribution.some(b => b.count > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tradeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {tradeDistribution.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No data.</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Holding Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-mono-data text-foreground mb-4">
              Avg: {formatDuration(holdingTimeStats.avg)}
            </p>
            {holdingTimeStats.data.length > 0 && holdingTimeStats.data.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={holdingTimeStats.data}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center">No data.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Post-Mortem Analysis Section ─── */}
      {worstTrades.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[hsl(var(--warning))]" />
            <h3 className="text-lg font-semibold text-foreground">Post-Mortem Analysis</h3>
            <span className="text-xs text-muted-foreground">• Worst {worstTrades.length} trade{worstTrades.length > 1 ? 's' : ''}</span>
          </div>
          {worstTrades.map(trade => (
            <TradePostMortem key={trade.id} trade={trade} />
          ))}
        </div>
      )}

      {/* Recent Trades Table */}
      <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
        <CardHeader>
          <CardTitle className="text-lg">Recent Trades</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedPositions.length > 0 ? (
            <div className="max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer" onClick={() => toggleSort('date')}>
                      Date {sortField === 'date' && (sortDir === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead>Entry</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => toggleSort('pnl')}>
                      PnL {sortField === 'pnl' && (sortDir === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedPositions.slice(0, 50).map(p => {
                    const dur = p.closed_at
                      ? (new Date(p.closed_at).getTime() - new Date(p.opened_at).getTime()) / (1000 * 60 * 60)
                      : 0;
                    return (
                      <TableRow key={p.id}>
                        <TableCell className="text-sm">{new Date(p.closed_at || p.opened_at).toLocaleDateString()}</TableCell>
                        <TableCell className="font-medium font-mono-data">{p.symbol}</TableCell>
                        <TableCell>
                          <Badge variant={p.side === 'buy' ? 'default' : 'destructive'} className="capitalize text-xs">
                            {p.side}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono-data">${p.average_entry_price.toFixed(2)}</TableCell>
                        <TableCell className={`font-mono-data font-medium ${p.realized_pnl >= 0 ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
                          ${p.realized_pnl.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{formatDuration(dur)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No closed trades.</p>
          )}
        </CardContent>
      </Card>

      {/* Strategy Comparison */}
      {allPerformances.length >= 2 && (
        <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-lg">Compare Strategies</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Strategy</TableHead>
                  <TableHead>Sharpe</TableHead>
                  <TableHead>Drawdown</TableHead>
                  <TableHead>Win Rate</TableHead>
                  <TableHead>Net PnL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allPerformances.map((p: any) => (
                  <TableRow key={p.id} className={p.strategy_instance_id === instanceId ? 'bg-accent/30' : ''}>
                    <TableCell className="font-medium">{(p.strategy_instances as any)?.strategy_name ?? 'Unknown'}</TableCell>
                    <TableCell className="font-mono-data">{p.sharpe_ratio?.toFixed(2)}</TableCell>
                    <TableCell className="text-destructive font-mono-data">{(p.max_drawdown * 100)?.toFixed(1)}%</TableCell>
                    <TableCell className="font-mono-data">{(p.win_rate * 100)?.toFixed(1)}%</TableCell>
                    <TableCell className={`font-mono-data ${p.net_profit >= 0 ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>${p.net_profit?.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StrategyAnalyticsPage;
